<!-- eslint-disable no-console -->
<script setup lang="ts">
import CalendarPreferences from '@/pages/CalendarPreferences.vue'
import router from '@/router'
import {
  mountCalendarSettingsApp,
  unmountCalendarSettingsApp,
} from '@/utils/remoteAppIntegration'
import {
  AlertSquareIcon,
  Announcement02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { CalendarLocationSettingsService } from '../class/services/CalendarLocationSettingsService'
import AppState, {
  fetchCalendarLocationSettings,
  isEquipmentEnabled,
  isRoomEnabled,
  isServiceCalendarEnabled,
} from '../states/app'
import Availability from './Availability.vue'
import CalendarSettingsWizard from './CalendarSettingsWizard.vue'
const { t } = useI18n()

const route = useRoute()
const showBanner = ref(false)
const calendarScheduleApp = ref<any>('')
const showArchivedServicesTab = computed(() => {
  const hasServiceCalendars = AppState.globalCalendars?.calendars?.some(
    calendar => calendar.type === 'service_booking'
  )
  return (
    hasServiceCalendars ||
    isRoomEnabled() ||
    isEquipmentEnabled() ||
    isServiceCalendarEnabled()
  )
})

const isHidden = computed(() => {
  return !showArchivedServicesTab.value
})

const tabOptions = computed(() => {
  return [
    {
      name: t('settings.calendar.calendars'),
      key: 'calendars',
      hidden: false,
    },
    {
      name: t('archivedServices.archivedServiceCalendars'),
      key: 'archived',
      hidden: isHidden.value,
    },
    {
      name: t('settings.preferences.preferences'),
      key: 'preferences',
      hidden: false,
    },
    {
      name: t('settings.availability.my_availability'),
      key: 'availability',
      hidden: false,
    },
  ]
})

onMounted(() => {
  if (route.query.section) {
    AppState.selectedSettingsTab = route.query.section as string
  }
  AppState.parentApp?.then(parent => {
    parent.emit('update-selected-tab', AppState.selectedSettingsTab)
  })
})

onBeforeUnmount(() => {
  unmountCalendarScheduleApp()
})

watch(
  () => [AppState.locationId, AppState.user, AppState.selectedSettingsTab],
  () => {
    if (!AppState.isScheduleEnabled) {
      return
    }
    if (
      AppState.selectedSettingsTab === 'availability' &&
      AppState.locationId
    ) {
      setTimeout(() => {
        mountCalendarScheduleApp()
      }, 300)
    } else {
      unmountCalendarScheduleApp()
    }
  }
)
const switchToCalendarsTab = () => {
  AppState.selectedSettingsTab = 'calendars'
}

const unmountCalendarScheduleApp = () => {
  unmountCalendarSettingsApp('CalendarSchedule')
  calendarScheduleApp.value = null
}

const mountCalendarScheduleApp = () => {
  unmountCalendarScheduleApp()
  const data = {
    route: route,
    router: router,
    locale: AppState.locale,
    locationId: AppState.locationId,
    userId: AppState.user?.id,
    isFullWidth: false,
    token: AppState.tokenId,
    source: 'my_availability',
    subSource: 'new_availability_page',
    loggedInUserInfo: {
      ...AppState.loggedInUserDetail,
      ...AppState.user,
    },
    authToken: AppState.authApiKey,
    redirectFunction: switchToCalendarsTab,
    showSaveButtonAtTop: true,
    showSaveButtonAtBottom: false,
    timezone: AppState.locationTimezone,
  }
  mountCalendarSettingsApp('CalendarSchedule', {
    ...data,
  })
}

const onClickTab = (tab: string) => {
  AppState.selectedSettingsTab = tab
  AppState.parentApp?.then(parent => {
    parent.emit('update-selected-tab', tab)
  })
}

const showAdvancedSettings = computed(() => {
  return (
    !AppState.showAdvancedCalendarSettings &&
    !AppState.showAdvancedServiceMenuSettings
  )
})

const openServiceSettings = () => {
  AppState.parentApp?.then(parent => {
    parent.emit('open-service-settings', {})
  })
}

const onClickDonotShowAgain = async () => {
  try {
    showBanner.value = false
    await CalendarLocationSettingsService.updateSettings(AppState.locationId, {
      showArchivedServicesBanner: false,
    })
    await fetchCalendarLocationSettings()
  } catch (error) {
    console.error(error)
  }
}

const showArchivedServicesBanner = computed(() => {
  return showBanner.value && showAdvancedSettings.value
})

watch(
  () => AppState.calendarLocationDetails,
  () => {
    showBanner.value = Boolean(
      AppState.calendarLocationDetails?.showArchivedServicesBanner
    )
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <div
      class="calendarSettingsContainer"
      :class="{
        'px-[32px] py-[20px]': showAdvancedSettings,
      }"
    >
      <div
        v-if="showAdvancedSettings"
        class="flex flex-row items-center justify-between"
        :style="{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'transparent',
        }"
      >
        <div class="flex flex-row gap-[8px]">
          <div v-for="(tab, index) in tabOptions" :key="index">
            <div
              v-if="!tab.hidden"
              :id="`tab-${tab.key}`"
              class="w-fit cursor-pointer rounded-[4px] px-[10px] py-[8px] font-medium"
              :class="{
                'bg-primary-50 text-primary-700':
                  AppState.selectedSettingsTab === tab.key,
                'bg-transparent text-gray-500':
                  AppState.selectedSettingsTab !== tab.key,
              }"
              role="button"
              @click="onClickTab(tab.key)"
              @keydown="onClickTab(tab.key)"
            >
              {{ tab.name }}
            </div>
          </div>
        </div>
      </div>

      <div
        :class="{
          'mt-[16px]': showAdvancedSettings,
        }"
        :style="{
          height: !showAdvancedSettings ? '100%' : 'calc(100vh - 100px)',
          overflow:
            AppState.isScheduleEnabled &&
            AppState.selectedSettingsTab === 'availability'
              ? 'hidden'
              : 'auto',
        }"
      >
        <div
          v-if="
            AppState.selectedSettingsTab === 'calendars' ||
            AppState.selectedSettingsTab === 'archived'
          "
        >
          <div
            v-if="
              AppState.selectedSettingsTab === 'archived' &&
              showArchivedServicesBanner
            "
            class="mb-[15px] mt-[20px] flex w-full flex-col items-start justify-start gap-1 rounded-lg border border-primary-300 bg-primary-25 px-[10px] py-[10px]"
          >
            <div class="flex flex-row items-center gap-2">
              <AlertSquareIcon class="h-5 w-5 text-primary-700" />
              <div class="flex flex-col gap-1">
                <div class="text-[13px] font-normal leading-6 text-gray-700">
                  {{ t('archivedServices.servicesBanner') }}
                </div>
              </div>
            </div>
            <div class="flex w-full flex-row !items-end !justify-between gap-2">
              <div
                class="flex cursor-pointer flex-row items-center gap-2 rounded-[4px] px-[10px] py-[8px]"
                role="button"
                @click="onClickDonotShowAgain"
                @keydown="onClickDonotShowAgain"
              >
                <div class="text-[14px] font-medium text-gray-700">
                  {{ $t('service_menu_advanced.dont_show_again') }}
                </div>
              </div>
              <div
                class="flex cursor-pointer flex-row items-center gap-2 rounded-[4px] border border-primary-300 bg-primary-50 px-[10px] py-[5px]"
                role="button"
                @click="openServiceSettings"
                @keydown="openServiceSettings"
              >
                <Announcement02Icon class="h-5 w-5 text-primary-700" />
                <div class="text-[14px] font-medium text-primary-700">
                  {{ t('archivedServices.check_new_services') }}
                </div>
              </div>
            </div>
          </div>
          <CalendarSettingsWizard />
        </div>
        <div v-if="AppState.selectedSettingsTab === 'preferences'">
          <CalendarPreferences />
        </div>
        <div v-if="AppState.selectedSettingsTab === 'availability'">
          <div
            v-if="AppState.isScheduleEnabled"
            id="CalendarSchedule"
            ref="calendarScheduleApp"
          />
          <Availability v-else />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.calendarSettingsContainer {
  font-family: 'Inter' !important;

  .n-card.n-modal {
    padding: 16px !important;
  }
}
</style>
